import { useRouter } from 'next/router'
import { useCallback, useMemo } from 'react'

import { Button } from 'src/components/atoms/Button'
import { ButtonLinkProps } from 'src/components/atoms/Link'

import { CtaButton, InternalLink } from 'src/config/generated/sanitySchema'
import { useMessenger } from 'src/hooks/useMessenger'
import { interpolatePathTokens } from 'src/utils/interpolatePathTokens'

import { CallToActionButtonLink } from '../CallToActionButtonLink'

export interface CmsCtaButtonProps
  extends Omit<CtaButton, '_type' | 'internalLink' | 'color' | 'variant'> {
  internalLink: InternalLink | undefined
  size?: ButtonLinkProps['size']
  variant?: ButtonLinkProps['variant']
  color?: ButtonLinkProps['color']
  className?: string
}

export const CmsCtaButton = ({
  text,
  externalUrl,
  internalLink,
  linkType,
  variant = 'contained',
  actionType,
  color = 'primary',
  size = 'base',
  className
}: CmsCtaButtonProps) => {
  const { showNewMessage } = useMessenger()
  const { query } = useRouter()
  const href = useMemo(() => {
    if (linkType === 'internal') {
      // Resolve any `{token}` placeholders (e.g. tier/frequency) from the
      // current route/query so one CMS link adapts per page.
      return internalLink?.path
        ? interpolatePathTokens(internalLink.path, query)
        : undefined
    }
    if (linkType === 'external') {
      return externalUrl
    }
    return null
  }, [externalUrl, internalLink?.path, linkType, query])

  const handleOnClick = useCallback(() => {
    if (linkType === 'action') {
      switch (actionType) {
        case 'messenger':
          showNewMessage()
          break
        default:
          break
      }
    }
  }, [actionType, linkType, showNewMessage])

  if (linkType === 'action') {
    return (
      <Button
        onClick={handleOnClick}
        color={color}
        size={size}
        variant={variant}
        className={className}
      >
        {text}
      </Button>
    )
  }

  return (
    <CallToActionButtonLink
      isExternal={linkType === 'external'}
      color={color}
      href={href || ''}
      size={size}
      variant={variant}
      className={className}
    >
      {text}
    </CallToActionButtonLink>
  )
}
